import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { getAvatarFont, getAvatarInitialsColor } from '../../base/avatar/components/web/styles';
import { getLocalParticipant, getParticipantDisplayName } from '../../base/participants/functions';
import { isTrackStreamingStatusActive } from '../../connection-indicator/functions';
import { getDisplayNameColor } from '../../display-name/components/web/styles';
import { getThumbnailBackgroundColor } from '../../filmstrip/functions.web';
import { getLargeVideoParticipant } from '../../large-video/functions';
import { isPrejoinPageVisible } from '../../prejoin/functions.any';
import { handlePiPLeaveEvent, handlePipEnterEvent, handleWindowBlur } from '../actions';
import { getPiPVideoTrack } from '../functions';
import logger from '../logger';
import { useCanvasAvatar } from '../useCanvasAvatar';
const useStyles = makeStyles()(() => {
    return {
        hiddenVideo: {
            position: 'absolute',
            width: '1px',
            height: '1px',
            opacity: 0,
            pointerEvents: 'none',
            left: '-9999px',
            top: '-9999px'
        }
    };
});
/**
 * Component that renders a hidden video element for Picture-in-Picture.
 * Automatically switches between real video track and canvas-based avatar
 * depending on video availability.
 *
 * @returns {JSX.Element} The hidden video element.
 */
const PiPVideoElement = () => {
    const { classes, theme } = useStyles();
    const videoRef = useRef(null);
    const previousTrackRef = useRef(null);
    // Redux selectors.
    const isOnPrejoin = useSelector(isPrejoinPageVisible);
    const localParticipant = useSelector(getLocalParticipant);
    const largeVideoParticipant = useSelector(getLargeVideoParticipant);
    // Use local participant during prejoin, otherwise large video participant.
    const participant = isOnPrejoin ? localParticipant : largeVideoParticipant;
    // Get appropriate video track based on prejoin state.
    const videoTrack = useSelector((state) => getPiPVideoTrack(state, participant));
    const displayName = useSelector((state) => participant?.id
        ? getParticipantDisplayName(state, participant.id)
        : '');
    const customAvatarBackgrounds = useSelector((state) => state['features/dynamic-branding']?.avatarBackgrounds || []);
    const dispatch = useDispatch();
    const avatarFont = getAvatarFont(theme);
    const fontFamily = avatarFont.fontFamily ?? 'Inter, sans-serif';
    const initialsColor = getAvatarInitialsColor(theme);
    const displayNameColor = getDisplayNameColor(theme);
    const { canvasStreamRef, canvasStreamReady } = useCanvasAvatar({
        participant,
        displayName,
        customAvatarBackgrounds,
        backgroundColor: getThumbnailBackgroundColor(theme),
        fontFamily,
        initialsColor,
        displayNameColor
    });
    // Determine if we should show avatar instead of video.
    const shouldShowAvatar = !videoTrack
        || videoTrack.muted
        || (!videoTrack.local && !isTrackStreamingStatusActive(videoTrack));
    /**
     * Effect: Handle switching between real video track and canvas avatar stream.
     */
    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) {
            return;
        }
        const previousTrack = previousTrackRef.current;
        // Detach previous track.
        if (previousTrack?.jitsiTrack) {
            try {
                previousTrack.jitsiTrack.detach(videoElement);
            }
            catch (error) {
                logger.error(`[画中画] 卸载旧轨道失败: ${error?.message || error}`, error);
            }
        }
        if (shouldShowAvatar) {
            const canvasStream = canvasStreamRef.current;
            // 等待画布首帧就绪，避免 PiP 打开时 srcObject 仍为空 → 黑屏。
            if (!canvasStreamReady || !canvasStream) {
                previousTrackRef.current = videoTrack;
                return;
            }
            if (videoElement.srcObject !== canvasStream) {
                videoElement.srcObject = canvasStream;
            }
            videoElement.play().catch((error) => {
                logger.warn(`[画中画] 头像视频 play 失败: ${error?.message || error}`);
            });
        }
        else if (videoTrack?.jitsiTrack) {
            videoElement.srcObject = null;
            videoTrack.jitsiTrack.attach(videoElement)
                .then(() => videoElement.play())
                .catch((error) => {
                logger.error(`[画中画] 挂载视频轨道失败: ${error?.message || error}`, error);
            });
        }
        previousTrackRef.current = videoTrack;
        return () => {
            if (videoTrack?.jitsiTrack && videoElement) {
                try {
                    videoTrack.jitsiTrack.detach(videoElement);
                }
                catch (error) {
                    logger.error(`[画中画] 清理轨道失败: ${error?.message || error}`, error);
                }
            }
        };
    }, [videoTrack, shouldShowAvatar, canvasStreamReady, canvasStreamRef]);
    /**
     * Effect: 失焦/切后台自动进 PiP。
     * 注意：不要在 window focus 时自动退出——手机点击进 PiP 后页面仍会 focus，
     * 会导致小窗闪一下立刻关掉；退出交给 leavepictureinpicture / 用户关闭小窗。
     */
    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) {
            return;
        }
        const onWindowBlur = () => dispatch(handleWindowBlur(videoElement));
        const onVisibilityChange = () => {
            if (document.hidden) {
                onWindowBlur();
            }
        };
        window.addEventListener('blur', onWindowBlur);
        document.addEventListener('visibilitychange', onVisibilityChange);
        const checkFocusAndEnterPiP = () => {
            if (!document.hasFocus()) {
                onWindowBlur();
            }
        };
        if (videoElement.readyState >= 1) {
            checkFocusAndEnterPiP();
        }
        else {
            videoElement.addEventListener('loadedmetadata', checkFocusAndEnterPiP, { once: true });
        }
        return () => {
            window.removeEventListener('blur', onWindowBlur);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            videoElement.removeEventListener('loadedmetadata', checkFocusAndEnterPiP);
        };
    }, [dispatch]);
    /**
     * Effect: PiP enter/leave event listeners.
     */
    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) {
            return;
        }
        const onEnterPiP = () => {
            logger.info(`[画中画] ${new Date().toISOString()} 已进入系统画中画`);
            dispatch(handlePipEnterEvent());
        };
        const onLeavePiP = () => {
            logger.info(`[画中画] ${new Date().toISOString()} 已离开系统画中画`);
            dispatch(handlePiPLeaveEvent());
        };
        videoElement.addEventListener('enterpictureinpicture', onEnterPiP);
        videoElement.addEventListener('leavepictureinpicture', onLeavePiP);
        return () => {
            videoElement.removeEventListener('enterpictureinpicture', onEnterPiP);
            videoElement.removeEventListener('leavepictureinpicture', onLeavePiP);
        };
    }, [dispatch]);
    return (React.createElement("video", { autoPlay: true, className: classes.hiddenVideo, id: 'pipVideo', muted: true, playsInline: true, ref: videoRef }));
};
export default PiPVideoElement;
